/**
 * Signed pass codes for badges, tickets and door checks.
 *
 * A pass code is `FDP1.<tag>.<guestId>.<signature>`. It carries an id, never a name or any personal detail, so a
 * lost or photographed badge reveals nothing. The signature is an HMAC-SHA-256 of the tag and id under a secret key
 * kept in the project file, so a code can be checked offline with no server, and a made-up code is rejected.
 *
 * Honest limit: anyone who holds the project file (and so the key) can make valid codes. That is right for a
 * single organiser checking a door from their own device; accounts with server-held keys come later in EventForge.
 *
 * Uses Web Crypto, which is available in every current browser and in Node 20+.
 */

const PREFIX = "FDP1";
const SIG_CHARS = 14; // 14 base64url characters = 84 bits
const enc = new TextEncoder();

function b64url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A new random secret for a project, 256 bits. */
export function newPassKey(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return b64url(bytes);
}

export const isValidPassKey = (k: unknown): k is string => typeof k === "string" && /^[A-Za-z0-9_-]{20,64}$/.test(k);

async function hmac(key: string, message: string): Promise<Uint8Array> {
  const k = await globalThis.crypto.subtle.importKey("raw", enc.encode(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return new Uint8Array(await globalThis.crypto.subtle.sign("HMAC", k, enc.encode(message)));
}

/** A short public fingerprint of the key, so a scanner can tell "other event" from "forged". */
export async function projectTag(key: string): Promise<string> {
  const h = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", enc.encode(`tag:${key}`)));
  return b64url(h).slice(0, 6);
}

export async function makePassCode(key: string, guestId: string): Promise<string> {
  if (!/^[A-Za-z0-9_-]{1,40}$/.test(guestId)) throw new Error("A pass id may only use letters, digits, _ and -.");
  const tag = await projectTag(key);
  const sig = b64url(await hmac(key, `${PREFIX}.${tag}.${guestId}`)).slice(0, SIG_CHARS);
  return `${PREFIX}.${tag}.${guestId}.${sig}`;
}

export interface ParsedPass { tag: string; guestId: string; signature: string }

/** Splits a scanned string into its parts without checking the signature. Null when it is not a pass code at all. */
export function parsePassCode(code: string): ParsedPass | null {
  const m = /^FDP1\.([A-Za-z0-9_-]{6})\.([A-Za-z0-9_-]{1,40})\.([A-Za-z0-9_-]{14})$/.exec(code.trim());
  return m ? { tag: m[1], guestId: m[2], signature: m[3] } : null;
}

export type PassCheck =
  | { ok: true; guestId: string }
  | { ok: false; reason: "format" | "other-project" | "signature" };

function sameText(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export async function verifyPassCode(code: string, key: string): Promise<PassCheck> {
  const p = parsePassCode(code);
  if (!p) return { ok: false, reason: "format" };
  if (p.tag !== (await projectTag(key))) return { ok: false, reason: "other-project" };
  const expected = b64url(await hmac(key, `${PREFIX}.${p.tag}.${p.guestId}`)).slice(0, SIG_CHARS);
  return sameText(expected, p.signature) ? { ok: true, guestId: p.guestId } : { ok: false, reason: "signature" };
}
